/**
 * Phase 6.6 — AI Action Panel
 *
 * Reusable widget that drops into any workflow-heavy page. Given a
 * short subject + structured context, asks the backend to suggest
 * 3–6 next actions. Response uses the [[link:...]] / [[action:...]]
 * tag grammar that the shared TaggedText renderer knows how to turn
 * into clickable buttons.
 *
 * Usage:
 *   <AIActionPanel
 *     subject="Candidate Jane Smith"
 *     context={{ candidate: { ... }, credentials: [...] }}
 *   />
 *
 * Also lets the user type their own follow-up question about the
 * current context — that goes to the regular /ai/chat endpoint with
 * the context serialized as a system-injected userContext.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiApi, planTasksApi, PlanTaskGroup } from '../../lib/api';
import { TaggedText, useDisambig, ActionType } from './Tags';
import AITaskWizard from '../ActionPlan/AITaskWizard';
import Icon from '../ui/Icon';

interface Props {
  /** Short human-readable subject, e.g. "Candidate Jane Smith". */
  subject: string;
  /** Structured context the AI should reason over. Keep it small —
   *  Claude reads all of it on every call. */
  context: Record<string, unknown>;
  /** Optional fixed list of entity types the AI should prefer linking
   *  to (e.g. on a CandidateDetail page, ['candidate','job','policy']).
   *  Not strictly enforced — Claude is free to pick others if needed. */
  hintEntityTypes?: string[];
  /** Auto-generate suggestions on mount (and on subject change) instead of
   *  waiting for a button click. Only enabled on the candidate profile. */
  autoLoad?: boolean;
}

export default function AIActionPanel({ subject, context, autoLoad = false }: Props) {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [wizardGoal, setWizardGoal] = useState<string | null>(null);
  const [groups, setGroups] = useState<PlanTaskGroup[]>([]);
  const { onDisambiguate, element: disambigModal } = useDisambig();

  // Auto-load suggestions so the user never has to click — and refresh
  // whenever the subject changes (e.g. navigating to a different candidate),
  // clearing the previous subject's suggestions first.
  const lastSubject = useRef<string | null>(null);
  useEffect(() => {
    if (!autoLoad || !subject || lastSubject.current === subject) return;
    lastSubject.current = subject;
    setSuggestions(null);
    setExpanded(false);
    void generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject]);

  async function generate() {
    setLoading(true); setError(null);
    try {
      const [sug, gRes] = await Promise.all([
        aiApi.suggestActions({ subject, context }),
        planTasksApi.listGroups().catch(() => ({ data: { groups: [] } })),
      ]);
      setSuggestions(sug.data.suggestions);
      setGroups(gRes.data.groups);
      setExpanded(true);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'AI unavailable.');
    } finally { setLoading(false); }
  }

  function onInvokeAction(action: ActionType, label: string) {
    if (action === 'create_task') { setWizardGoal(label); return; }
    if (action === 'send_esign')  { navigate(`/esign/documents/new?recipient_name=${encodeURIComponent(label)}`); return; }
    if (action === 'draft_email') { navigate(`/ai-assistant?prompt=${encodeURIComponent(label)}`); return; }
  }

  return (
    // Tailwind: the panel used a hardcoded white background, which turned
    // into a glaring white slab on the dark dashboard. It now sits on the
    // normal card surface with a faint AI-tinted edge, so it reads as one of
    // the dashboard's cards that happens to be AI-flavoured — the violet
    // stays as the accent (title, icon, primary button), not the whole box.
    // my-4, not mt-3: the panel had no bottom margin at all, so it sat flush
    // against the KPI grid that follows it on every dashboard. Equal margins
    // above and below keep it in the page's 24px vertical rhythm.
    <div
      className="my-4 border border-ai-tint bg-surface"
      // Geometry matches the shared Card (.ui-card + .ui-card__body): this panel
      // sits beside real <Card>s on the candidate tabs, and Tailwind's rounded-xl
      // (12px) / p-3.5 (14px) left it visibly smaller-cornered and tighter than
      // its neighbours. The AI-tinted border stays — that is what marks it as AI.
      style={{ borderRadius: 'var(--r-lg)', padding: 'var(--sp-5)' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: expanded ? 12 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="sparkles" size={18} className="text-ai" />
          <div>
            <div className="text-[13px] font-bold text-ai">AI suggestions</div>
            <div className="text-[11px] text-muted">Context-aware next actions for this page.</div>
          </div>
        </div>
        {!suggestions && (
          <button onClick={() => void generate()} disabled={loading}
            className="flex cursor-pointer items-center gap-1.5 rounded-md border-none
                       bg-ai px-3.5 py-1.5 text-xs font-bold text-on-brand
                       transition-opacity hover:opacity-90 disabled:opacity-60">
            {loading ? <span className="ui-btn__spinner" aria-hidden /> : <Icon name="sparkles" size={13} />}
            {loading ? 'Thinking…' : 'Suggest actions'}
          </button>
        )}
        {suggestions && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => { setSuggestions(null); setExpanded(false); }}
              style={{ padding: '5px 10px', background: 'var(--surface-subtle)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, fontWeight: 600, color: 'var(--text)', cursor: 'pointer' }}>
              Clear
            </button>
            <button onClick={() => void generate()} disabled={loading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', background: 'var(--ai-tint)', color: 'var(--ai)', border: '1px solid var(--ai-border)', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading
                ? <><span className="ui-btn__spinner" aria-hidden /> Regenerating…</>
                : <><Icon name="refresh" size={12} /> Regenerate</>}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div style={{ padding: 10, background: 'var(--danger-tint)', border: '1px solid var(--danger-border)', color: 'var(--danger-fg)', borderRadius: 6, fontSize: 12, marginTop: 10 }}>
          {error}
        </div>
      )}

      {expanded && suggestions && (
        <div
          className="ai-suggestions"
          aria-busy={loading}
          style={{ background: 'var(--surface-sunken)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-4)', fontSize: 'var(--fs-base)', lineHeight: 'var(--lh-relaxed)', color: 'var(--text)', // Regenerating keeps the previous answer on screen (it is still
            // valid) but dims it, so the spinner in the button is not the
            // only sign that a new one is coming.
            opacity: loading ? 0.55 : 1, transition: 'opacity var(--dur) var(--ease-out)' }}
        >
          <TaggedText content={suggestions} onInvokeAction={onInvokeAction} onDisambiguate={onDisambiguate} />
        </div>
      )}

      {disambigModal}

      {wizardGoal != null && (
        <AITaskWizard
          groups={groups}
          initialGoal={wizardGoal}
          onCreated={() => { /* stay on this page, nothing to do */ }}
          onClose={() => setWizardGoal(null)}
        />
      )}
    </div>
  );
}
